import { useQuery } from "@tanstack/react-query";
import {
  Check,
  LoaderCircle,
  Minus,
  PackageCheck,
  Plus,
  ShoppingBag
} from "lucide-react";
import { useState } from "react";
import {
  Link,
  useParams
} from "react-router";

import {
  ProductImageGallery
} from "../components/product/ProductImageGallery";
import { useCart } from "../context/CartContext";
import {
  getProduct,
  getPublicProductImageUrl
} from "../services/catalog";

export function ProductDetailsPage() {
  const { addItem } = useCart();
  const { slug } = useParams();
  const [quantity, setQuantity] = useState(1);

  const productQuery = useQuery({
    queryKey: ["product", slug],
    queryFn: () => getProduct(slug!),
    enabled: Boolean(slug)
  });

  if (productQuery.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoaderCircle
          className="animate-spin text-[#70845b]"
          size={38}
        />
      </div>
    );
  }

  if (
    productQuery.isError ||
    !productQuery.data
  ) {
    return (
      <div className="rini-container py-24 text-center">
        <h1 className="rini-heading text-5xl">
          Product not found
        </h1>

        <Link
          to="/shop"
          className="rini-button-primary mt-8"
        >
          Return to shop
        </Link>
      </div>
    );
  }

  const product =
    productQuery.data;

  const hamperItems =
    product.hamperItems ?? [];

  const isGiftHamper =
    product.category.slug ===
      "gift-hampers" &&
    hamperItems.length > 0;

  const isOutOfStock =
    product.stockQuantity <= 0;

  const isLowStock =
    product.stockQuantity > 0 &&
    product.stockQuantity <= 5;

  const maxQuantity =
    Math.max(
      0,
      product.stockQuantity
    );

  return (
    <main className="py-8 sm:py-12 lg:py-20">
      <div className="rini-container">
        <div className="mb-6 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-[#746d65] sm:mb-8 sm:text-sm">
          <Link to="/">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/shop">Shop</Link>
          <span className="mx-2">/</span>
          <span>{product.name}</span>
        </div>

        <div className="grid gap-8 sm:gap-10 lg:grid-cols-2 lg:gap-12">
          <ProductImageGallery
            productName={product.name}
            images={product.images ?? []}
          />

          <div className="lg:py-6">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#70845b]">
              {product.category.name}
            </p>

            <h1 className="rini-heading mt-3 text-4xl font-semibold leading-[1.08] sm:mt-4 sm:text-5xl">
              {product.name}
            </h1>

            <p className="mt-4 text-base leading-7 text-[#746d65] sm:mt-5 sm:text-lg sm:leading-8">
              {product.shortDescription}
            </p>

            <div className="mt-7 flex items-center gap-3">
              <span className="text-3xl font-semibold text-[#5e3b2c]">
                ₹{Number(product.price).toLocaleString("en-IN")}
              </span>

              {product.compareAtPrice && (
                <span className="text-lg text-[#746d65] line-through">
                  ₹{Number(product.compareAtPrice).toLocaleString("en-IN")}
                </span>
              )}
            </div>

            <div
              className={[
                "mt-7 flex items-center gap-2 text-sm font-medium",
                isOutOfStock
                  ? "text-red-700"
                  : isLowStock
                    ? "text-amber-700"
                    : "text-[#526746]"
              ].join(" ")}
            >
              <Check size={18} />

              {isOutOfStock
                ? "Currently out of stock"
                : isLowStock
                  ? `Only ${product.stockQuantity} handcrafted ${product.stockQuantity === 1 ? "piece" : "pieces"} available`
                  : "Available in stock"}
            </div>

            {product.weightGrams && (
              <p className="mt-4 text-sm text-[#746d65]">
                Net weight: {product.weightGrams} g
              </p>
            )}

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
              <div className="flex min-h-12 w-full items-center justify-between rounded-full border border-[#5e3b2c]/20 sm:w-auto">
                <button
                  type="button"
                  onClick={() =>
                    setQuantity((current) =>
                      Math.max(1, current - 1)
                    )
                  }
                  className="flex h-12 w-14 items-center justify-center"
                  aria-label="Reduce quantity"
                >
                  <Minus size={18} />
                </button>

                <span className="min-w-10 text-center font-semibold">
                  {quantity}
                </span>

                <button
                  type="button"
                  disabled={
                    isOutOfStock ||
                    quantity >=
                      maxQuantity
                  }
                  onClick={() =>
                    setQuantity((current) =>
                      Math.min(
                        maxQuantity,
                        current + 1
                      )
                    )
                  }
                  className="flex h-12 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-35"
                  aria-label="Increase quantity"
                >
                  <Plus size={18} />
                </button>
              </div>

              <button
                type="button"
                disabled={isOutOfStock}
                onClick={() => {
                  if (!isOutOfStock) {
                    addItem(
                      product,
                      quantity
                    );
                  }
                }}
                className="rini-button-primary min-h-13 w-full justify-center text-base disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-56"
              >
                <ShoppingBag size={18} />

                {isOutOfStock
                  ? "Out of stock"
                  : "Add to cart"}
              </button>
            </div>

            {!isOutOfStock &&
              quantity >= maxQuantity &&
              maxQuantity > 0 && (
                <p className="mt-3 text-sm font-medium text-amber-700">
                  Maximum available quantity reached.
                </p>
              )}

            <div className="mt-10 border-t border-[#5e3b2c]/10 pt-8">
              <h2 className="font-serif text-2xl text-[#38251e]">
                Product story
              </h2>

              <p className="mt-4 leading-8 text-[#746d65]">
                {product.description}
              </p>
            </div>

            <div className="mt-8 rounded-3xl bg-[#edf1e8] p-6">
              <h3 className="font-serif text-xl text-[#38251e]">
                Making video
              </h3>

              <p className="mt-2 leading-7 text-[#746d65]">
                RiNi preparation and packaging videos will appear
                here when they are uploaded through the admin panel.
              </p>
            </div>
          </div>
        </div>

        {isGiftHamper && (
          <section className="mt-20 border-t border-[#5e3b2c]/10 pt-16">
            <div className="mx-auto max-w-3xl text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
                <PackageCheck
                  size={25}
                  strokeWidth={1.5}
                />
              </span>

              <p className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                Curated by RiNi
              </p>

              <h2 className="rini-heading mt-3 text-4xl font-semibold sm:text-5xl">
                What's inside this hamper
              </h2>

              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-[#746d65]">
                A thoughtfully selected collection of RiNi favourites,
                brought together for a meaningful gifting experience.
              </p>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {hamperItems.map(
                (item) => {
                  const includedProduct =
                    item.product;

                  const primaryImage =
                    includedProduct.images?.find(
                      (image) =>
                        image.isPrimary
                    ) ??
                    includedProduct.images?.[0];

                  return (
                    <Link
                      key={
                        item.productId
                      }
                      to={
                        `/products/${includedProduct.slug}`
                      }
                      className="group overflow-hidden rounded-[2rem] border border-[#5e3b2c]/10 bg-[#fffdf9] transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-[#eee5d9]">
                        {primaryImage ? (
                          <img
                            src={
                              getPublicProductImageUrl(
                                primaryImage.id
                              )
                            }
                            alt={
                              primaryImage.altText ??
                              includedProduct.name
                            }
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <span className="font-serif text-3xl text-[#6f5140]">
                              RiNi
                            </span>
                          </div>
                        )}

                        <span className="absolute right-4 top-4 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-[#5e3b2c] shadow-sm">
                          Qty {item.quantity}
                        </span>
                      </div>

                      <div className="p-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.17em] text-[#70845b]">
                          {
                            includedProduct.category.name
                          }
                        </p>

                        <h3 className="mt-2 font-serif text-2xl leading-tight text-[#38251e]">
                          {
                            includedProduct.name
                          }
                        </h3>

                        {includedProduct.shortDescription && (
                          <p className="mt-3 line-clamp-2 text-sm leading-6 text-[#746d65]">
                            {
                              includedProduct.shortDescription
                            }
                          </p>
                        )}

                        <div className="mt-5 flex items-center justify-between border-t border-[#5e3b2c]/10 pt-4">
                          <span className="text-sm font-semibold text-[#5e3b2c]">
                            View product
                          </span>

                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f3e8da] text-[#5e3b2c] transition group-hover:bg-[#5e3b2c] group-hover:text-white">
                            →
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                }
              )}
            </div>

            <div className="mt-10 rounded-[2rem] bg-[#edf1e8] px-6 py-7 text-center sm:px-10">
              <p className="font-serif text-2xl text-[#38251e]">
                Beautifully brought together, ready for gifting.
              </p>

              <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-[#746d65]">
                The hamper price shown above is for the complete curated
                collection. Individual product prices are not added separately.
              </p>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
